import { ChangeEvent, FC, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { Modal, Select } from '@/shared/ui';
import { ValueType } from '@/shared/ui/Select/Select.types';

import { notificationModel } from '@/entities/notification';
import { organizationModel } from '@/entities/organization';
import { transcriptorModel } from '@/entities/transcriptor';

import { i18n } from '../../config';

import styles from './TransferModal.module.scss';

type TransferModalProps = {
  className?: string;
  isOpen: boolean;
  consultationId: string;
  patientId: string;
  onClose: () => void;
};

export const TransferModal: FC<TransferModalProps> = (props) => {
  const { isOpen, consultationId, patientId, onClose } = props;

  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  const organizationUsers = useAppSelector(
    organizationModel.selectors.selectAllUsers,
  );

  const [selectedDoctorId, setSelectedDoctorId] = useState<string>('');

  const doctorOptions = useMemo(
    () =>
      organizationUsers.map((user) => ({
        value: user.UserID,
        label:
          `${user.PersonalData?.FirstName || ''} ${user.PersonalData?.LastName || ''}`.trim() ||
          user.PersonalData?.Email ||
          user.UserID,
      })),
    [organizationUsers],
  );

  const handleSubmit = async () => {
    if (!selectedDoctorId) {
      return;
    }

    try {
      await dispatch(
        transcriptorModel.thunks.transferConsultation({
          consultationId,
          patientId,
          doctorUserId: selectedDoctorId,
        }),
      ).unwrap();

      onClose();
    } catch {
      dispatch(
        notificationModel.actions.addNotification({
          id: Date.now().toString(),
          type: 'error',
          message: formatMessage(
            i18n.consultationOptionsMessages.transferError,
          ),
        }),
      );
    }
  };

  return (
    <Modal
      data-component="transfer-consultation-modal"
      isOpen={isOpen}
      title={formatMessage(i18n.consultationOptionsMessages.transferTitle)}
      onCancel={onClose}
      onOk={handleSubmit}
      okButtonText={formatMessage(
        i18n.consultationOptionsMessages.transferConfirmButton,
      )}
      cancelButtonText={formatMessage(
        i18n.consultationOptionsMessages.cancelButton,
      )}
      okButtonProps={{ disabled: !selectedDoctorId }}
    >
      <div className={styles.container}>
        <p className={styles.label}>
          {formatMessage(i18n.consultationOptionsMessages.transferSelectDoctor)}
        </p>
        <Select
          value={selectedDoctorId}
          onChange={(newValue: ChangeEvent | ValueType) => {
            if (typeof newValue === 'string') {
              setSelectedDoctorId(newValue);
            }
          }}
          options={doctorOptions}
          placeholder={formatMessage(
            i18n.consultationOptionsMessages.transferSelectDoctor,
          )}
        />
      </div>
    </Modal>
  );
};
